<template>

  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="$emit('close')">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                         leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-95 transition-opacity"/>
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <TransitionChild as="template" enter="ease-out duration-300"
                         enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                         enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                         leave-from="opacity-100 translate-y-0 sm:scale-100"
                         leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
              class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
            <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
              <button
                  type="button"
                  class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  @click="$emit('close')"
              >
                <span class="sr-only">Close</span>
                <XIcon class="h-6 w-6" aria-hidden="true"/>
              </button>
            </div>

            <div class="mt-3 text-center sm:mt-0  sm:text-left">
              <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
                Invite team members
              </DialogTitle>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  Share this workspace with team members to view or edit.
                </p>

                <div v-for="(sponsoredUser, idx) in userList" :key="'sponsored-user-`${idx}`'"
                     class="my-6 text-sm font-medium ">
                  <div class="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-3 ">
                    <div :class="{'sm:w-3/12': isWorkspaceOwnerMember, 'sm:w-4/12': !isWorkspaceOwnerMember}">
                      <label :for="`email-${idx}`" class="block text-gray-700 font-semibold pb-2"
                             :class="{ 'sm:sr-only': idx >= 1}">Email</label>
                      <div class="mt-1" :class="[{'relative rounded-md shadow-sm': !sponsoredUser.isValidEmail}]">
                        <input
                            v-model="userList[idx].email"
                            @input="debounceCheckEmail(idx)"
                            :id="`email-${idx}`"
                            :class="[{'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500 pr-10': !sponsoredUser.isValidEmail, 'bg-green-50': sponsoredUser.status === CREATED_SPONSORSHIP, 'bg-red-50': sponsoredUser.status === ERROR_SPONSORSHIP}]"
                            type="email"
                            name="email"
                            class="shadow-sm focus:ring-primary focus:border-primary block w-full border-gray-300 rounded-md text-sm"
                            placeholder="nom@example.com"
                            aria-invalid="true"
                            aria-describedby="email-error"
                        />
                        <div v-show="!sponsoredUser.isValidEmail"
                             class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <ExclamationCircleIcon class="h-5 w-5 text-red-500" aria-hidden="true"/>
                        </div>
                      </div>
                      <p v-show="!sponsoredUser.isValidEmail" class="mt-2 text-sm text-red-600" id="email-error">Email
                        is not valid.</p>
                    </div>

                    <div :class="{'sm:w-3/12': isWorkspaceOwnerMember, 'sm:w-4/12': !isWorkspaceOwnerMember}">
                      <label :for="`name-${idx}`" class="block text-gray-700 font-semibold pb-2"
                             :class="{ 'sm:sr-only': idx >= 1}">Name</label>
                      <div class="mt-1" :class="[{'relative rounded-md shadow-sm': !sponsoredUser.isValidName}]">
                        <input
                            v-model="userList[idx].name"
                            @input="debounceCheckName(idx)"
                            type="text"
                            name="name"
                            :id="`name-${idx}`"
                            class="shadow-sm focus:ring-primary focus:border-primary block w-full border-gray-300 rounded-md  text-sm"
                            :class="[{'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500 pr-10': !sponsoredUser.isValidName, 'bg-green-50': sponsoredUser.status === CREATED_SPONSORSHIP, 'bg-red-50': sponsoredUser.status === ERROR_SPONSORSHIP}]"
                            placeholder="Optional"
                            aria-invalid="true"
                            aria-describedby="name-error"
                        />
                        <div v-show="!sponsoredUser.isValidName"
                             class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <ExclamationCircleIcon class="h-5 w-5 text-red-500" aria-hidden="true"/>
                        </div>
                      </div>
                      <p v-show="!sponsoredUser.isValidName" class="mt-2 text-sm text-red-600" id="name-error">Keep
                        empty or put a valid name.</p>
                    </div>

                    <div class="sm:w-3/12">
                      <label :for="`language-${idx}`" class="block text-gray-700 font-semibold pb-2"
                             :class="{ 'sm:sr-only': idx >= 1}">Message language</label>
                      <select
                          v-model="userList[idx].language"
                          :id="`language-${idx}`"
                          :class="[{'bg-green-50': sponsoredUser.status === CREATED_SPONSORSHIP, 'bg-red-50': sponsoredUser.status === ERROR_SPONSORSHIP}]"
                          class="w-full mt-1 block pl-3 pr-10 py-2 border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md text-sm"
                      >
                        <option v-for="(language, idx) in emailLanguage" :key="'email-language-`${idx}`'"
                                :value="language.code" :selected="language === defaultEmailLanguage">{{
                            language.label
                          }}
                        </option>
                      </select>
                    </div>

                    <div class="sm:w-2/12 text-center" v-if="isWorkspaceOwnerMember === true">
                      <label :for="`transfer-ownership-${idx}`" class="block text-gray-700 font-semibold pb-2"
                             :class="{ 'sm:sr-only': idx >= 1}">New Owner</label>
                      <div
                          :class="['flex items-center justify-center', { 'h-auto': idx === 0, 'h-full': idx !== 0}]">
                        <input
                            v-model="userList[idx].newWorkspaceOwner"
                            @click="newWorkspaceOwnerUniqueness(idx)"
                            :value="idx"
                            type="checkbox"
                            name="transfer-ownership"
                            :id="`transfer-ownership-${idx}`"
                            class="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                            :class="[{'sm:mt-3.5' : idx === 0, 'bg-green-50': sponsoredUser.status === CREATED_SPONSORSHIP, 'bg-red-50': sponsoredUser.status === ERROR_SPONSORSHIP}]"
                            placeholder="Optional"
                        />
                      </div>
                    </div>

                    <div class="sm:w-1/12 flex align-bottom justify-center">
                      <button
                          v-if="userList[idx].status !== CREATED_SPONSORSHIP"
                          @click="removeUser(idx)"
                          :class="{'sm:pt-8' : idx === 0, 'pt-2' : idx > 0}"
                          type="button"
                          class="inline-flex items-center pb-8 sm:pb-0"
                      >
                        <PlusIcon class="h-6 w-6 transform rotate-45" aria-hidden="true"/>
                      </button>

                      <div
                          v-else
                          :class="{'sm:pt-8' : idx === 0, 'pt-2' : idx > 0}"
                          class="inline-flex items-center text-green-500  pb-8 sm:pb-0"
                      >
                        <CheckIcon class="h-8 w-8" aria-hidden="true"/>
                      </div>

                    </div>

                  </div>
                  <p v-show="sponsoredUser.errorMsg !== null && sponsoredUser.errorMsg.length > 0"
                     class="mt-2 text-sm text-red-600">{{ sponsoredUser.errorMsg }}</p>

                </div>

                <div class="sm:w-1/2 pt-4">
                  <button
                      @click="addUser()"
                      type="button"
                      class="inline-flex items-center text-secondary text-sm"
                  >
                    <PlusCircleIcon class="h-5 w-5" aria-hidden="true"/>
                    &nbsp;Add another one
                  </button>
                </div>


              </div>
            </div>


            <div class="mt-16 sm:flex sm:flex-row-reverse">
              <button
                  @click="invite()"
                  :class="{'cursor-not-allowed ': hasAtLeastOneError || hasNoEntry, 'hover:bg-ternary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ternary-500': !hasAtLeastOneError && !hasNoEntry}"
                  type="button"
                  class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-ternary-500 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm"
                  :disabled="hasAtLeastOneError || hasNoEntry"
              >
                Send invitations
              </button>
              <button
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ternary-500 sm:mt-0 sm:w-auto sm:text-sm"
                  @click="cleanData(); $emit('close')"
              >
                Finish
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

</template>

<script>
import _debounce from 'lodash.debounce'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {CheckIcon, ExclamationIcon, PlusCircleIcon, PlusIcon, XCircleIcon, XIcon} from '@heroicons/vue/outline'
import {ExclamationCircleIcon} from '@heroicons/vue/solid'
import {mapGetters} from "vuex";
import _get from "lodash/get";

const PENDING_SPONSORSHIP = 0;
const CREATED_SPONSORSHIP = 1;
const ERROR_SPONSORSHIP = 2;

export default {
  name: "SponsorshipForm",
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    ExclamationIcon,
    XIcon,
    ExclamationCircleIcon,
    PlusIcon,
    PlusCircleIcon,
    CheckIcon,
    XCircleIcon
  },
  props: {
    open: Boolean,
    defaultEmailLanguage: {
      type: String,
      default: 'en_GB',
    },
    emailLanguage: {
      type: Object,
      default: function () {
        return [
          {'code': 'en_GB', 'label': 'English'},
          {'code': 'ja_JP', 'label': 'Japanese'},
        ];
      }
    },
  },
  setup() {
    return {
      PENDING_SPONSORSHIP,
      CREATED_SPONSORSHIP,
      ERROR_SPONSORSHIP,
    }
  },
  data() {
    return {

      userList: [],
      messages: [
        {key: "201", value: "Invitation sent"},
        {key: "400", value: "Internal error"},
        {key: "401", value: "You are unauthorized"},
        {key: "403", value: "You are unauthorized"},
        {key: "409", value: "You have already invited this person"},
        {key: "415", value: "Internal error"},
        {key: "422", value: "This person already has an account"},
        {key: "500", value: "Internal error"},
      ],

    }
  },
  created() {
    this.addUser();
  },
  methods: {
    debounceCheckEmail: _debounce(function (userIdx) {
      this.checkEmail(userIdx);
    }, 400),
    debounceCheckName: _debounce(function (userIdx) {
      this.checkName(userIdx);
    }, 400),
    checkEmail(userIdx) {
      let regexEmail = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      this.userList[userIdx].isValidEmail = regexEmail.test(this.userList[userIdx].email);
    },
    checkName(userIdx) {
      let regexName = /^$|^[^!"#$%&()*+,/:;<=>?@\[\]^_`{|}~]{3,64}$/;
      this.userList[userIdx].isValidName = regexName.test(this.userList[userIdx].name);
    },

    addUser() {
      this.userList.push({
        'email': '',
        'name': '',
        'language': this.defaultEmailLanguage,
        'type': 'collaborator', // Use by default for the moment. TODO: Need in the future to define all use cases
        'newWorkspaceOwner': false,
        'isValidName': true,
        'isValidEmail': true,
        'status': PENDING_SPONSORSHIP,
        'errorMsg': null,
      });
    },

    removeUser(idx) {
      this.userList.splice(idx, 1);
      if (this.userList.length === 0) {
        this.addUser();
      }
    },

    newWorkspaceOwnerUniqueness(userIdx) {
      let idx = -1;
      for (const userListElement of this.userList) {
        ++idx;
        if (idx !== userIdx) {
          this.userList[idx].newWorkspaceOwner = false;
        }
      }
    },

    invite() {
      let idx = -1;
      for (const userToInvite of this.userList) {
        ++idx;

        if (userToInvite.status !== CREATED_SPONSORSHIP) {
          // Valid the user before send request
          userToInvite.errorMsg = null;
          this.checkEmail(idx);
          this.checkName(idx);
          if (!userToInvite.isValidEmail || !userToInvite.isValidName) {
            continue;
          }

          // It's ok on front
          this.$axios.post(
              this.$apiUrl + '/sponsorships',
              {
                email: userToInvite.email,
                name: userToInvite.name.trim() === '' ? null : userToInvite.name,
                locale: userToInvite.language,
                type: userToInvite.type,
                workspace: this.workspace,
                newWorkspaceOwner: userToInvite.newWorkspaceOwner,
              },
              {}
          ).then(function (response) {
            if (response.status === 201 || response.status === 200) {
              userToInvite.status = CREATED_SPONSORSHIP;
            }
          }).catch(function (error) {
            userToInvite.status = ERROR_SPONSORSHIP;
            userToInvite.errorMsg = this.getMessage(error.response.status);
            if (error.response.status === 400) {
              userToInvite.errorMsg += ': ' + error.response.violations[0].message;
            }
          }.bind(this));

        }
      }
    },

    cleanData() {
      this.userList = [];
      this.addUser();
    },

    getMessage(key) {
      key = '' + key;
      return this.messages.find(item => {
        return item.key === key;
      })["value"];
    },

    ...mapGetters({
      currentMemberProfile: 'getCurrentMemberProfile',
      currentWorkspaceSlug: 'getCurrentWorkspaceSlug',
      selectedWorkspace: 'getWorkspaceForSponsorshipPopup',
      getMemberProfile: 'getCurrentMemberProfile',
    }),
  },
  computed: {
    hasNoEntry() {
      for (const userListElement of this.userList) {
        if (userListElement.name.length !== 0 || userListElement.email.length !== 0) {
          return false;
        }
      }

      return true;
    },
    hasAtLeastOneError() {
      for (const userListElement of this.userList) {
        if (!userListElement.isValidName || !userListElement.isValidEmail) {
          return true;
        }
      }

      return false;
    },

    isWorkspaceOwnerMember() {
      return _get(this.getMemberProfile(), 'workspaceAdmin', false);
    },

    workspace() {
      const selectedWorkspace = this.selectedWorkspace();
      return (selectedWorkspace === null || selectedWorkspace.slug.trim().length === 0) ? this.currentWorkspaceSlug() : selectedWorkspace.slug;
    },
  }
}
</script>
